import { faAt, faPhone } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { categoryMap } from "../config/CategoryMap";
import { useChat } from "../context/ChatContext";
import { ROUTES, buildRoute } from "../routes/RouteNames";
import makeSlug from "../utils/makeSlug";
import { buildUserProfilePath } from "../utils/userProfile";

import { CertificateIcon } from "../assets/icons";
import { ASSETS } from "../config/assets";
import { useSocialProof } from "../hooks/useSocialProof";
import { Stars } from "./Stars";
import SocialProof from "./SocialProof";
import "./UserBasicInfo.css";

const UserBasicInfo = ({
    currentUser, // info o tom, zda jsem majitel profilu/inzerátu
    profileImage,
    isActive,
    email: initialEmail,
    phone: initialPhone,
    obs_email, // Zašifrovaný email z API
    obs_phone, // Zašifrovaný telefon z API
    phoneVisible,
    isTechDetail, // rozliší mód zobrazení
    firstName, // Jméno majitele inzerátu
    lastName, // Příjmení majitele inzerátu
    specs = [], // Specializace (pole)
    userId, // ID uživatele pro proklik na profil
    techTitle, // Název techniky (pro zobrazení v tech odpovědi na inzerát)
    techId,
    socialProofInitially,
    rating, // Hodnocení uživatele (hvězdičky)
    reviewsCount, // Počet recenzí uživatele
    isVerified, // Zda je uživatel ověřen
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { openChatWith } = useChat();

    // LOGIKA PRO OCHRANU KONTAKTŮ proti spambottům
    const [isRevealed, setIsRevealed] = useState(false);

    // Funkce pro "odprášení" dat
    const deobfuscate = (str) => {
        if (!str || !str.startsWith("v1_")) return str;
        try {
            const encoded = str.replace("v1_", "").split("").reverse().join("");
            return atob(encoded); // Base64 decode
        } catch (e) {
            return "Chyba zobrazení";
        }
    };

    const handleRevealClick = async (e) => {
        e.stopPropagation(); // Abychom neklikli na kartu pod tím
        setIsRevealed(true);
    };

    // Pokud jsem majitel, vidím údaje hned. Pokud ne, vidím buď tlačítko nebo načtená data.
    const displayEmail = currentUser ? initialEmail : deobfuscate(obs_email);
    const displayPhone = currentUser ? initialPhone : deobfuscate(obs_phone);

    // LOGIKA TOOLTIPU: Určení textu podle situace
    let tooltipText = "";
    if (currentUser) {
        tooltipText = isTechDetail
            ? "Nemůžete se ozvat na vlastní inzerát"
            : "Nemůžete poslat nabídku sami sobě";
    } else if (!isActive && !isTechDetail) {
        tooltipText = "Uživatel momentálně nehledá práci";
    }
    const isUnclickable = !!tooltipText;

    // Sestavení specializací: "Osvětlovač | Stagehands", nebo "Člen komunity"
    const formattedSpecs = specs.length
        ? specs.map((s) => categoryMap[s.name] || s.name).join(" | ")
        : "Člen komunity";

    const handleProfileClick = () => {
        if (userId && firstName && lastName) {
            const fullName = `${firstName}-${lastName}`;
            const slug = makeSlug(fullName).replace(/_/g, "-");

            navigate(`/user/${userId}/${slug}`, {
                state: {
                    ...location.state, // Zachová, zda jsi přišel z PRACOVNÍCI > OSVĚTLOVAČ
                    userName: `${firstName} ${lastName}`, // Předá jméno pro Path
                },
            });
        } else if (userId) {
            navigate(buildRoute(ROUTES.USER_DETAIL, { id: userId }), {
                state: { ...location.state },
            });
        }
    };

    const handleShowListings = () => {
        navigate(buildRoute(ROUTES.USER_LISTINGS, { id: userId }), {
            state: {
                ...location.state,
                userName: `${firstName} ${lastName}`, // Aby breadcrumb věděl, čí nabídky to jsou
            },
        });
    };

    // Logika pro kliknutí na "Ozvat se" – otevře živý chat včetně kontextu (inzerát / profil)
    const handleMessageClick = () => {
        // Pokud je to můj profil nebo není aktivní/tech, nic neděláme (unclickable)
        if (currentUser || (!isActive && !isTechDetail)) return;

        // Otevře živý chat s protistranou (založí konverzaci, pokud ještě není)
        openChatWith(userId, buildConversationContext());
    };

    // Kontext, na který uživatel reaguje – zobrazí se jako banner v chatu
    const buildConversationContext = () => {
        if (isTechDetail) {
            return {
                subject_type: "item",
                subject_id: techId || null,
                context_title: techTitle || null,
                context_url: techId ? buildRoute(ROUTES.TECH_DETAIL, { id: techId }) : null,
            };
        }

        return {
            subject_type: "user",
            subject_id: userId || null,
            context_title: `${firstName || ""} ${lastName || ""}`.trim() || null,
            context_url: buildUserProfilePath({ id: userId, first_name: firstName, last_name: lastName }),
        };
    };

    const {
        stats: socialProof,
        loading: socialProofLoading,
    } = useSocialProof({
        type: "item",
        targetId: techId || null,
        initialStats: socialProofInitially,
    });

    return (
        <>
            <div
                className={`user_basic_info ${isTechDetail ? "tech_mode" : ""}`}
            >
                <div
                    className={`user_basic_info_profile_picture ${isTechDetail ? "small_avatar clickable" : ""}`}
                    onClick={isTechDetail ? handleProfileClick : undefined}
                >
                    <img
                        src={profileImage || ASSETS.default_avatar}
                        alt="avatar"
                    />
                </div>

                <div className="user_basic_info_details">
                    {/* 2) a 3) Jméno a specializace */}
                    {isTechDetail ? (
                        <div
                            className="owner_info clickable"
                            onClick={handleProfileClick}
                        >
                            <div className="rating_row">
                                {rating && rating > 0 ? (
                                    <>
                                        <div className="stars">
                                            <Stars rating={rating} />
                                        </div>
                                        <p className="stars_number">{rating}</p>
                                    </>
                                ) : (
                                    <p className="no_rating">Nehodnocen(a)</p>
                                )}
                            </div>

                            <h3 className="owner_name">
                                {firstName} {lastName}
                                {isVerified && <CertificateIcon className="certificate_icon--normal" />}
                            </h3>

                            {isTechDetail && (
                                <SocialProof
                                    stats={socialProof}
                                    loading={socialProofLoading}
                                    position="user_basic_info"
                                />
                            )}

                            {formattedSpecs && (
                                <p className="owner_specs">{formattedSpecs}</p>
                            )}
                        </div>
                    ) : (
                        <>
                            {isActive ? (
                                <h3 className="status_active">HLEDÁ PRÁCI</h3>
                            ) : (
                                <h3 className="status_inactive">
                                    NEHLEDÁ PRÁCI
                                </h3>
                            )}
                        </>
                    )}

                    {/* 4) Tlačítko Ozvat se s TOOLTIPEM */}
                    <div
                        className={`tooltip-wrapper ${isUnclickable ? "has-tooltip" : ""}`}
                    >
                        <button
                            type="button"
                            className={`form-submit ${isUnclickable ? "unclickable" : ""}`}
                            onClick={handleMessageClick}
                        >
                            <p className="strong">
                                {isTechDetail
                                    ? "Ozvat se na nabídku"
                                    : "Ozvat se s prací"}
                            </p>
                        </button>
                        {isUnclickable && (
                            <span className="tooltip-bubble">
                                {tooltipText}
                            </span>
                        )}
                    </div>

                    {/* 5) Druhé tlačítko */}
                    <button
                        className="secondary_button extra_space"
                        onClick={handleShowListings}
                    >
                        <p className="oauth_text strong">
                            {isTechDetail
                                ? "Zobrazit všechny nabídky"
                                : "Zobrazit nabídky techniky"}
                        </p>
                    </button>

                    {/* Kontakt: Email 
                    <div className="user_basic_info_contact">
                        <FontAwesomeIcon icon={faAt} className="contact_icon" />
                        <p className="contact_text">{email}</p>
                    </div>

                    
                    {phone && phoneVisible ? (
                        <div className="user_basic_info_contact">
                            <FontAwesomeIcon
                                icon={faPhone}
                                className="contact_icon"
                            />
                            <p className="contact_text">{phone}</p>
                        </div>
                    ) : null}*/}

                    <div className="contact_section_wrapper">
                        {!isRevealed && !currentUser ? (
                            <span className="options_span clickable" onClick={handleRevealClick}>
                                <p className="options_text">Zobrazit kontakt</p>
                            </span>
                        ) : (
                            <div className="contacts_visible_fadein">
                                <div className="user_basic_info_contact">
                                    <FontAwesomeIcon icon={faAt} className="contact_icon" />
                                    <p className="contact_text">{displayEmail}</p>
                                </div>

                                {displayPhone && (phoneVisible || currentUser) && (
                                    <div className="user_basic_info_contact">
                                        <FontAwesomeIcon icon={faPhone} className="contact_icon" />
                                        <p className="contact_text">{displayPhone}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default UserBasicInfo;
